import type { CSSProperties } from "react";
import {
  EMAIL_DOCUMENT_DEFAULT_BAND_BORDER_COLOR,
  EMAIL_DOCUMENT_DEFAULT_BAND_BORDER_WIDTH,
  EMAIL_DOCUMENT_MAX_BORDER_WIDTH,
} from "../schema/emailDocumentContract";
import type { BorderStyle, Padding } from "../domain/types";

export function defaultSurfaceBorder(): BorderStyle {
  return {
    width: { ...EMAIL_DOCUMENT_DEFAULT_BAND_BORDER_WIDTH },
    style: "none",
    color: EMAIL_DOCUMENT_DEFAULT_BAND_BORDER_COLOR,
  };
}

export function bandBorderDefaultWidth(): Padding {
  return { ...EMAIL_DOCUMENT_DEFAULT_BAND_BORDER_WIDTH };
}

export function clampBandBorderWidthValue(value: number): number {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    return 0;
  }

  if (value < 0) {
    return 0;
  }

  if (value > EMAIL_DOCUMENT_MAX_BORDER_WIDTH) {
    return EMAIL_DOCUMENT_MAX_BORDER_WIDTH;
  }

  return value;
}

function normalizeBandBorderWidth(width: Padding): Padding {
  return {
    top: clampBandBorderWidthValue(width.top),
    right: clampBandBorderWidthValue(width.right),
    bottom: clampBandBorderWidthValue(width.bottom),
    left: clampBandBorderWidthValue(width.left),
  };
}

export function bandBorderHasVisibleWidth(width: Padding): boolean {
  return width.top > 0 || width.right > 0 || width.bottom > 0 || width.left > 0;
}

export function bandBorderWidthIsDefault(width: Padding): boolean {
  const defaults = bandBorderDefaultWidth();
  return (
    width.top === defaults.top &&
    width.right === defaults.right &&
    width.bottom === defaults.bottom &&
    width.left === defaults.left
  );
}

export function bandBorderResolveColor(border: BorderStyle): string {
  return border.color || EMAIL_DOCUMENT_DEFAULT_BAND_BORDER_COLOR;
}

export function bandBorderAfterWidthChange(
  border: BorderStyle,
  nextWidth: Padding,
): BorderStyle {
  const width = normalizeBandBorderWidth(nextWidth);

  if (!bandBorderHasVisibleWidth(width)) {
    return defaultSurfaceBorder();
  }

  return {
    width,
    color: border.color || EMAIL_DOCUMENT_DEFAULT_BAND_BORDER_COLOR,
    style: border.style === "none" ? "solid" : border.style,
  };
}

export function bandBorderCss(border: BorderStyle): CSSProperties {
  if (!bandBorderHasVisibleWidth(border.width) || border.style === "none") {
    return {};
  }

  const color = bandBorderResolveColor(border);
  const { width } = border;

  return {
    boxSizing: "border-box",
    borderStyle: border.style,
    borderColor: color,
    borderTopWidth: width.top,
    borderRightWidth: width.right,
    borderBottomWidth: width.bottom,
    borderLeftWidth: width.left,
  };
}

export function bandBorderShouldSwapRightToBottom(width: Padding): boolean {
  return width.right > 0 && width.bottom === 0;
}

export function bandBorderForStackedMobile(border: BorderStyle): BorderStyle {
  if (!bandBorderShouldSwapRightToBottom(border.width)) {
    return border;
  }

  return {
    ...border,
    width: {
      top: border.width.top,
      right: 0,
      bottom: border.width.right,
      left: border.width.left,
    },
  };
}
